/**
 * Example Word Card Component
 * Displays a word with image, phonetic, and meaning
 */
import { PhonicsExample } from '../../data/natural-phonics';
import { getWordEmoji } from '../../data/phonics-images';

interface ExampleWordCardProps {
  example: PhonicsExample;
  style: 'american' | 'british';
  showPhonetic?: boolean;
  onPlay?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export default function ExampleWordCard({
  example,
  style,
  showPhonetic = true,
  onPlay,
  size = 'md',
}: ExampleWordCardProps) {
  const phonetic = style === 'american' ? example.americanPhonetic : example.britishPhonetic;
  const isDifferent = example.americanPhonetic !== example.britishPhonetic;
  const emoji = getWordEmoji(example.word);

  const sizeClasses = {
    sm: {
      card: 'p-3',
      emoji: 'text-2xl',
      word: 'text-lg',
      phonetic: 'text-xs',
    },
    md: {
      card: 'p-4',
      emoji: 'text-3xl',
      word: 'text-xl',
      phonetic: 'text-sm',
    },
    lg: {
      card: 'p-6',
      emoji: 'text-4xl',
      word: 'text-2xl',
      phonetic: 'text-base',
    },
  };

  const classes = sizeClasses[size];

  return (
    <div
      className={`bg-white rounded-2xl shadow-soft ${classes.card} flex items-center gap-4 hover:shadow-md transition-all ${
        isDifferent ? 'border-2 border-yellow-200' : ''
      }`}
    >
      {/* Emoji */}
      <span className={classes.emoji}>{emoji}</span>

      {/* Word info */}
      <div className="flex-1">
        <p className={`font-bold text-gray-800 ${classes.word}`}>{example.word}</p>
        <p className="text-sm text-gray-500">{example.meaning}</p>
      </div>

      {/* Phonetic */}
      {showPhonetic && (
        <div className="text-right">
          <p className={`font-mono text-primary ${classes.phonetic}`}>{phonetic}</p>
          {isDifferent && (
            <div className="flex gap-1 mt-1">
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                🇺🇸 {example.americanPhonetic}
              </span>
              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                🇬🇧 {example.britishPhonetic}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Play button */}
      {onPlay && (
        <button
          onClick={onPlay}
          className="text-2xl hover:scale-110 transition-transform"
          title="播放发音"
        >
          🔊
        </button>
      )}
    </div>
  );
}
